import { useCallback, useEffect, useState } from 'react'
import { Button, DatePicker, Divider, Form, Modal, Select, Space, TableProps, Tooltip } from 'antd'
import { BlockOutlined, ClockCircleOutlined, EyeOutlined, MoneyCollectOutlined, SearchOutlined, UndoOutlined } from '@ant-design/icons'
import CustomTable from '../../components/table'
import defaultParams from '../../assets/constants/default_params'
import { useLoading } from '../../context/LoadingContext'
import { apiAddTransactionConnectionByOrderId, apiGetListCompanies, apiGetListOrderTransactionConnection, apiUpdateOrderTransactionConnection } from '../../api'
import { filterOptionByLabel } from '../../utils/filter-utils'
import { mbhStatusOptions } from '../../assets/constants/default_options'
import { ICompany } from '../../models/company.modal'
import { renderDateByUnix, renderType } from '../kit-sim/method'
import { useSelector } from 'react-redux'
import { authSelector } from '../../redux/slices/authSlice'
import { DETAIL, KIT_ORDER, ORDER } from '../../assets/constants/route_path'
import { Link } from 'react-router-dom'
import { toastError, toastSuccess } from '../../utils/toast'
import { renderOrderStatus } from '../quanlytopup'

export default function Order() {
    const [companyOptions, setCompanyOptions] = useState<{ label: string, value: Number }[]>([])
    const [transactionConnection, setTransactionConnection] = useState<any>({})
    const [currentPage, setCurrentPage] = useState<number>(defaultParams.PAGE)
    const [pageSize, setPageSize] = useState<number>(defaultParams.LIMIT)
    const [search, setSearch] = useState<any>({})
    const [modalStatus, setModalStatus] = useState<{ data: any | null, isOpen: boolean }>({
        data: null,
        isOpen: false,
    })
    const [modalLock, setModalLock] = useState<{ data: any | null, isOpen: boolean }>({
        data: null,
        isOpen: false,
    })
    const { setIsLoading } = useLoading()
    const [formSearch] = Form.useForm()
    const { account } = useSelector(authSelector)
    const [isBlocking, setIsBlocking] = useState<boolean>(false)

    const orderTransactionConnectionColumns: TableProps<any>['columns'] = [
        {
            key: 'cpCode',
            title: 'Mã công ty',
            dataIndex: 'cpCode',
        },
        {
            key: 'ownerBy',
            title: 'Nhân sự đấu nối',
            dataIndex: 'ownerBy',
        },
        {
            key: 'dataPack',
            title: 'Gói cước',
            dataIndex: 'dataPack',
        },
        {
            key: 'type',
            title: 'Loại gói cước',
            dataIndex: 'type',
            render: type => renderType(type),
        },
        {
            key: 'totalSim',
            title: 'Tổng SIM',
            dataIndex: 'totalSim',
        },
        {
            key: 'totalSuccess',
            title: 'Tổng thành công',
            dataIndex: 'totalSuccess',
        },
        {
            key: 'totalFail',
            title: 'Tổng thất bại',
            dataIndex: 'totalFail',
        },
        {
            key: 'startDate',
            title: 'Ngày đấu nối',
            dataIndex: 'startDate',
            render: startDate => renderDateByUnix(startDate),
        },
        {
            key: 'status',
            title: 'Trạng thái',
            dataIndex: 'status',
            render: status => renderOrderStatus(status),
        },
        (
            account?.canGetOrderTransactionConnectionDetail
            || account?.canAddTransactionConnectionByOrderId
            || account?.canUpdateOrderTransactionConnection
        ) ? {
            key: 'actions',
            title: 'Tác vụ',
            render: data => {
                return (
                    <Space>
                        {account?.canGetOrderTransactionConnectionDetail && (
                            <Tooltip title="Chi tiết đơn hàng">
                                <Link to={KIT_ORDER + ORDER + DETAIL + `?orderId=${data.orderId}`}>
                                    <Button
                                        size='small'
                                        icon={<EyeOutlined />}
                                        style={{ backgroundColor: '#52c41a', color: '#fff' }}
                                    />
                                </Link>
                            </Tooltip>
                        )}
                        {account?.canAddTransactionConnectionByOrderId && (
                            isBlocking && data.status === 'CREATED' ? (
                                <Tooltip title="Chờ đơn hàng khác">
                                    <Button disabled icon={<ClockCircleOutlined />} />
                                </Tooltip>
                            ) : (
                                <Tooltip title="Đấu nối đơn hàng">
                                    <Button
                                        disabled={data.status !== 'CREATED'}
                                        onClick={() => setModalStatus({ data, isOpen: true })}
                                        size='small'
                                        icon={<MoneyCollectOutlined />}
                                        type='primary'
                                    />
                                </Tooltip>
                            )
                        )}
                        {account?.canUpdateOrderTransactionConnection && (
                            <Tooltip title="Khoá đơn hàng">
                                <Button
                                    disabled={data.status === 'LOCKED' || data.status !== 'CREATED'}
                                    onClick={() => setModalLock({ data, isOpen: true })}
                                    size='small'
                                    icon={<BlockOutlined />}
                                    type='primary'
                                    danger
                                />
                            </Tooltip>
                        )}
                    </Space>
                )
            },
        } : {},
    ]

    const fetchCompanies = useCallback(async () => {
        try {
            const resp = await apiGetListCompanies({ limit: defaultParams.MAX_LIMIT })
            if (resp.data != null) {
                const options = resp.data.companies.map((item: ICompany) => ({
                    label: item.name,
                    value: item.code,
                }))
                setCompanyOptions(options)
            } else {
                setCompanyOptions([])
            }
        } catch (error) {
            console.log(error)
        }
    }, [])

    useEffect(() => {
        fetchCompanies()
    }, [fetchCompanies])

    const fetchOrderTransactionConnection = useCallback(async () => {
        try {
            setIsLoading(true)
            const resp = await apiGetListOrderTransactionConnection({
                page: currentPage,
                size: pageSize,
                ...search,
            })
            if (resp.status === 200) {
                setIsBlocking(resp.data?.orders?.filter((item: any) => item.status === 'PROCESSING')?.length > 0)
                setTransactionConnection(resp.data)
            }
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }, [setIsLoading, currentPage, pageSize, search])

    useEffect(() => {
        fetchOrderTransactionConnection()
    }, [fetchOrderTransactionConnection])

    const handleSearch = ({ cpCode, status, date }: any) => {
        if (!cpCode && !status && !date) return
        let startDate, endDate
        if (date) {
            startDate = date[0] ? date[0]?.format('YYYY-MM-DD') : null
            endDate = date[1] ? date[1]?.format('YYYY-MM-DD') : null
        }
        setSearch({ cpCode, status, startDate, endDate })
        setCurrentPage(defaultParams.PAGE)
        setPageSize(defaultParams.LIMIT)
    }

    const handleClearSearch = () => {
        formSearch.resetFields()
        setSearch({})
        setCurrentPage(defaultParams.PAGE)
        setPageSize(defaultParams.LIMIT)
    }

    const handleAddTransactionConnectionByOrderId = async () => {
        try {
            setIsLoading(true)
            const resp = await apiAddTransactionConnectionByOrderId({ orderId: modalStatus.data.orderId })
            setTimeout(async () => {
                if (resp.status === 200) {
                    toastSuccess('Đang thực hiện đấu nối đơn hàng!')
                    fetchOrderTransactionConnection()
                    handleCloseModalAddTransactionConnectionByOrderId()
                } else {
                    toastError('Có lỗi xảy ra!')
                }
                setIsLoading(false)
            }, 8000);
        } catch (error) {
            console.log(error);
            toastError('Có lỗi xảy ra!')
            setIsLoading(false)
        }
    }

    const handleCloseModalAddTransactionConnectionByOrderId = () => {
        setModalStatus({ data: null, isOpen: false })
    }

    const handleLockOrderTransactionConnection = async ({ id, orderId, status }: any) => {
        try {
            setIsLoading(true)
            const resp = await apiUpdateOrderTransactionConnection({ order: { id, orderId, status } })
            if (resp.status === 200) {
                toastSuccess('Khoá đơn hàng thành công!')
                fetchOrderTransactionConnection()
                setModalLock({ data: null, isOpen: false })
            } else {
                toastError('Có lỗi xảy ra!')
            }
        } catch (error) {
            console.log(error);
            toastError('Có lỗi xảy ra!')
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div>
            <Form form={formSearch} onFinish={handleSearch}>
                <div style={{ display: 'flex', alignItems: 'start', flexWrap: 'wrap', gap: 20 }}>
                    <Form.Item name='cpCode' label='Công ty'>
                        <Select
                            style={{ minWidth: 170 }}
                            showSearch
                            placeholder="Chọn công ty"
                            options={companyOptions}
                            filterOption={filterOptionByLabel}
                        />
                    </Form.Item>
                    <Form.Item name='status' label='Trạng thái'>
                        <Select
                            style={{ width: 170 }}
                            showSearch
                            placeholder="Chọn trạng thái"
                            options={mbhStatusOptions}
                            filterOption={filterOptionByLabel}
                        />
                    </Form.Item>
                    <Form.Item name="date" label="Ngày đấu nối">
                        <DatePicker.RangePicker
                            format="YYYY-MM-DD"
                            picker="date" />
                    </Form.Item>
                    <Space style={{ marginBottom: 24 }}>
                        <Button type='primary' htmlType="submit" icon={<SearchOutlined />}>Tìm kiếm</Button>
                        <Button onClick={handleClearSearch} icon={<UndoOutlined />}></Button>
                    </Space>
                </div>
            </Form>
            <CustomTable
                namePage='đơn hàng đấu nối'
                columns={orderTransactionConnectionColumns}
                dataSource={transactionConnection.orders}
                pageSize={pageSize}
                setPageSize={setPageSize}
                total={transactionConnection.total}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
            />
            <Modal title='Đấu nối cho đơn hàng' open={modalStatus.isOpen}
                onCancel={handleCloseModalAddTransactionConnectionByOrderId}
                footer={[
                    <Button onClick={handleAddTransactionConnectionByOrderId}
                        style={{ background: '#008965', color: 'white' }}>
                        Xác nhận
                    </Button>,
                    <Button onClick={handleCloseModalAddTransactionConnectionByOrderId}
                        style={{ background: 'gray', color: 'white' }}>
                        Đóng
                    </Button>,
                ]}>
                <Divider />
                <p>Đấu nối cho đơn hàng <b>{modalStatus.data?.orderId}</b></p>
                <Divider />
            </Modal>
            <Modal title='Khoá đơn hàng' open={modalLock.isOpen}
                onCancel={(() => setModalLock({ data: null, isOpen: false }))}
                footer={[
                    <Button onClick={() => handleLockOrderTransactionConnection({ id: modalLock.data?.id, orderId: modalLock.data?.orderId, status: 'LOCKED' })}
                        type='primary' danger>
                        Xác nhận
                    </Button>,
                ]}>
                <Divider />
                <p>Khoá đơn hàng <b>{modalLock.data?.orderId}</b></p>
                <Divider />
            </Modal>
        </div>
    )
}
